export type GeoTagValue = "US/CN only" | "US/CN + EU" | "Global";

export type YesNo = "Yes" | "No";

export type RiskValue = "Low" | "Med" | "High";

export type InvestmentStrategyValue =
  | "Aggressive Growth"
  | "Growth"
  | "Balanced"
  | "Income"
  | "Capital Preservation";

export type MinNlvValue =
  | "$20K"
  | "$30K"
  | "$40K"
  | "$50K"
  | "$60K"
  | "$70K"
  | "$80K"
  | "$90K"
  | "$100K"
  | "$125K"
  | "$150K"
  | "$160K"
  | "$170K"
  | "$175K"
  | "$200K"
  | "$250K"
  | "$300K"
  | "$500K"
  | "$750K"
  | "$1M"
  | "$2M"
  | "$3M"
  | "$4M"
  | "$5M"
  | "$10M"
  | "$15M"
  | "$20M"
  | "$25M";

export interface ProductMetadata {
  geo_tag?: GeoTagValue;
  shorts_tag?: YesNo;
  last_updated_tag?: YesNo;
  min_number_positions_tag?: YesNo;
  leverage_tag?: YesNo;
  is_retirement_tag?: YesNo;
  min_nlv_tag?: MinNlvValue;
  tradeable_ibkr_tag?: YesNo;
  plutus_override_tag?: YesNo;
  investible_tag?: YesNo;
  risk_tag?: RiskValue;
  investment_strategy_tag?: InvestmentStrategyValue;
  mixId?: string;
}

export interface MetadataTagConfig {
  key: keyof ProductMetadata;
  label: string;
  options: readonly string[];
}

export interface MetadataTextConfig {
  key: keyof ProductMetadata;
  label: string;
  inputType: "text";
  placeholder?: string;
}

export type MetadataFieldConfig = MetadataTagConfig | MetadataTextConfig;

/**
 * Parses a MinNlvValue string (e.g. "$60K", "$2.5M") into a numeric USD amount.
 * Returns undefined when the tag is absent or unparseable.
 */
export function parseMinNlvTag(tag: MinNlvValue | undefined): number | undefined {
  if (!tag) return undefined;
  const raw = tag.replace(/[$,]/g, "");
  const match = raw.match(/^(\d+(?:\.\d+)?)(K|M)?$/i);
  if (!match) return undefined;
  const num = parseFloat(match[1]);
  const suffix = match[2]?.toUpperCase();
  if (suffix === "M") return num * 1_000_000;
  if (suffix === "K") return num * 1_000;
  return num;
}

/**
 * Maps RiskValue ("Low"/"Med"/"High") to a RiskLevel key for color coding.
 */
export function riskTagToLevel(tag: RiskValue | undefined): "low" | "medium" | "high" {
  if (!tag) return "medium";
  switch (tag) {
    case "Low":
      return "low";
    case "Med":
      return "medium";
    case "High":
      return "high";
    default:
      return "medium";
  }
}

export const METADATA_TAG_CONFIGS: MetadataTagConfig[] = [
  { key: "investible_tag", label: "Investible", options: ["Yes", "No"] as const },
  { key: "geo_tag", label: "Geography", options: ["US/CN only", "US/CN + EU", "Global"] as const },
  { key: "shorts_tag", label: "Shorts", options: ["Yes", "No"] as const },
  { key: "last_updated_tag", label: "Last Updated", options: ["Yes", "No"] as const },
  { key: "min_number_positions_tag", label: "Min Positions", options: ["Yes", "No"] as const },
  { key: "leverage_tag", label: "Leverage", options: ["Yes", "No"] as const },
  { key: "is_retirement_tag", label: "Retirement", options: ["Yes", "No"] as const },
  {
    key: "min_nlv_tag",
    label: "Min NLV",
    options: [
      "$20K", "$30K", "$40K", "$50K", "$60K", "$70K", "$80K", "$90K",
      "$100K", "$125K", "$150K", "$160K", "$170K", "$175K", "$200K", "$250K", "$300K",
      "$500K", "$750K", "$1M", "$2M", "$3M", "$4M", "$5M",
      "$10M", "$15M", "$20M", "$25M",
    ] as const,
  },
  { key: "tradeable_ibkr_tag", label: "Tradeable IBKR", options: ["Yes", "No"] as const },
  { key: "plutus_override_tag", label: "Plutus Override", options: ["Yes", "No"] as const },
  { key: "risk_tag", label: "Risk", options: ["Low", "Med", "High"] as const },
  {
    key: "investment_strategy_tag",
    label: "Strategy",
    options: [
      "Aggressive Growth", "Growth", "Balanced", "Income", "Capital Preservation",
    ] as const,
  },
];

export const METADATA_TEXT_CONFIGS: MetadataTextConfig[] = [
  {
    key: "mixId",
    label: "MixId",
    inputType: "text",
    placeholder: "mix_25_0_0_30_40_5",
  },
];

export const METADATA_FIELD_CONFIGS: MetadataFieldConfig[] = [
  ...METADATA_TAG_CONFIGS,
  ...METADATA_TEXT_CONFIGS,
];
